import { presetsTypes } from '@wix/fedops-presets'
import type { ViewerPlatformEssentials } from '@wix/fe-essentials-viewer-platform'
import type { FedopsConfig, Experiments } from '@wix/thunderbolt-symbols'
import {
	AppsMutingWhiteList,
	FEDOPS_WHITE_LIST,
	ThunderboltMutingBlackList,
	ThunderboltMutingWhiteList,
} from './constants'
// eslint-disable-next-line no-restricted-syntax
import type { FedopsLogger, IAppIdentifier } from '@wix/fedops-logger'
import { PanoramaPlatform, panoramaClientFactory } from '@wix/fe-essentials-viewer-platform/panorama-client'
import { getPanoramaGlobalConfig } from './panoramaConfig'

export type FedopsFactory = ViewerPlatformEssentials['createFedopsLogger']

export const createFedopsLogger = ({
	biLoggerFactory,
	customParams = {},
	phasesConfig = 'SEND_ON_FINISH',
	appName = 'thunderbolt',
	presetType = process.env.PACKAGE_NAME === 'thunderbolt-ds' ? presetsTypes.DS : presetsTypes.BOLT,
	reportBlackbox = false,
	paramsOverrides = {},
	factory,
	muteThunderboltEvents = false,
	experiments = {},
}: FedopsConfig & { factory: FedopsFactory; experiments?: Experiments }): FedopsLogger => {
	const fedopsLogger = factory(appName, {
		presetType,
		phasesConfig,
		isPersistent: true,
		isServerSide: !process.env.browser,
		reportBlackbox,
		customParams,
		biLoggerFactory,
		// @ts-ignore FEDINF-3725
		paramsOverrides,
		enableSampleRateForAppNames:
			!!experiments['specs.thunderbolt.fedops_enableSampleRateForAppNames'] ??
			(typeof window !== 'undefined' &&
				!!window?.viewerModel?.experiments['specs.thunderbolt.fedops_enableSampleRateForAppNames']),
	})

	const {
		interactionStarted,
		interactionEnded,
		appLoadingPhaseStart,
		appLoadingPhaseFinish,
		appLoadStarted,
		appLoaded,
	} = fedopsLogger

	const isPanoramaExperimentOpen = experiments['specs.thunderbolt.reportFedopsAndPanorama']

	const isWorker = () => process.env.browser && typeof window === 'undefined' // TODO: fix

	const isErrorReport = (overrides?: { evid?: string }) => overrides?.evid && parseInt(overrides.evid, 10) === 26

	const getPanoramaClient = () => {
		if (!process.env.browser || !isPanoramaExperimentOpen) {
			return null
		}

		const config = getPanoramaGlobalConfig()

		const isRollout = !!paramsOverrides.is_rollout
		const isHeadless = !!paramsOverrides.is_headless

		let msid = paramsOverrides.metaSiteId
		let dataCenter = 'unknown'
		let isCached

		if (!isWorker()) {
			msid = msid ?? window?.viewerModel?.site.metaSiteId ?? ''
			dataCenter = window?.viewerModel?.site.dc ?? ''
			isCached = !!window?.fedops?.is_cached
		}

		const panorama = panoramaClientFactory({
			baseParams: {
				platform: PanoramaPlatform.Viewer,
				msid,
				fullArtifactId: 'com.wixpress.html-client.wix-thunderbolt',
				componentId: appName,
			},
			data: {
				isRollout,
				isHeadless,
				customParams,
				presetType,
				dataCenter,
				isCached,
				isSsr: !process.env.browser,
			},
		}).withGlobalConfig(config)

		if (isWorker()) {
			panorama.withReporter((payload: any) => {
				const body = JSON.stringify({ messages: payload })

				fetch('https://panorama.wixapps.net/api/v1/bulklog', {
					method: 'POST',
					body,
					keepalive: true,
				}).catch((e) => {
					console.log(e)
				})

				return true
			})
		}

		return panorama.client()
	}

	const panoramaClient = getPanoramaClient()

	const shouldReportTransactionToPanorama = (eventName: string) => {
		return !FEDOPS_WHITE_LIST.has(eventName)
	}

	const reportPanoramaLoad = (isStartEvent: boolean, overrides?: any) => {
		if (!panoramaClient || isWorker()) {
			return
		}

		if (isStartEvent) {
			panoramaClient.reportLoadStart({ paramsOverrides: overrides })
		} else {
			panoramaClient.reportLoadFinish({ paramsOverrides: overrides })
		}
	}

	const reportPanoramaError = (overrides: { errorInfo?: string; errorType?: string } = {}) => {
		if (!panoramaClient) {
			return
		}

		const { errorInfo, errorType } = overrides
		const error = new Error(errorInfo)

		panoramaClient?.errorMonitor().reportError(error, {
			errorName: errorType,
			environment: 'Viewer',
		})
	}

	const reportPanoramaTransaction = (eventName: string, isStartEvent: boolean, overrides?: any) => {
		if (!panoramaClient || isWorker() || !shouldReportTransactionToPanorama(eventName)) {
			return
		}

		if (isStartEvent) {
			panoramaClient.transaction(eventName).start({ paramsOverrides: overrides })
		} else {
			panoramaClient.transaction(eventName).finish({ paramsOverrides: overrides })
		}
	}

	const shouldReportToFedops = (event: string, params?: IAppIdentifier, overrides?: any) => {
		// ONLY THUNDERBOLT (APP_ID===UNDEFINED) AND APPS THAT WERE WHITELISTED SHOULD BE SAMPLED
		const appShouldReportAll = params?.appId ? !AppsMutingWhiteList.has(params.appId) : false

		// muteThunderboltEvents = false => event shouldn't muted => event should be reported
		const shouldAlwaysReportEvent = ThunderboltMutingBlackList.has(event)
		const shouldAlwaysMuteEvent = ThunderboltMutingWhiteList.has(event)

		const isSiteAssetsModule = !!overrides?.siteAssetsModule
		if (isSiteAssetsModule) {
			// for specific site-assets requests without eventName
			return true
		}

		return shouldAlwaysReportEvent || appShouldReportAll || (!shouldAlwaysMuteEvent && !muteThunderboltEvents)
	}

	// This is done this way because FedopsLogger is a class and not an Object,
	// Therefor if we return an object it will crash because it operates on 'this' which does not exist in an object
	// so we can't make it immutable.

	fedopsLogger.interactionStarted = (interaction: string, params?) => {
		if (isErrorReport(params?.paramsOverrides)) {
			reportPanoramaError(params?.paramsOverrides)
		} else {
			reportPanoramaTransaction(interaction, true, params?.paramsOverrides)
		}

		if (shouldReportToFedops(interaction, undefined, params?.paramsOverrides)) {
			return interactionStarted.call(fedopsLogger, interaction, params)
		} else {
			try {
				performance.mark(`${interaction} started`)
			} catch {}
		}
		return { timeoutId: 0 }
	}

	fedopsLogger.interactionEnded = (interaction: string, params?) => {
		reportPanoramaTransaction(interaction, false, params?.paramsOverrides)

		if (shouldReportToFedops(interaction)) {
			interactionEnded.call(fedopsLogger, interaction, params)
		} else {
			try {
				performance.mark(`${interaction} ended`)
			} catch {}
		}
	}

	fedopsLogger.appLoadingPhaseStart = (phase: string, params?: IAppIdentifier) => {
		reportPanoramaTransaction(phase, true, params?.paramsOverrides)

		if (shouldReportToFedops(phase, params)) {
			appLoadingPhaseStart.call(fedopsLogger, phase, params)
		} else {
			try {
				performance.mark(`${phase} started`)
			} catch {}
		}
	}

	fedopsLogger.appLoadingPhaseFinish = (phase: string, params?: IAppIdentifier, options?: any) => {
		reportPanoramaTransaction(phase, false, params?.paramsOverrides)

		if (shouldReportToFedops(phase, params)) {
			appLoadingPhaseFinish.call(fedopsLogger, phase, params, options)
		} else {
			try {
				performance.mark(`${phase} finished`)
			} catch {}
		}
	}

	const isAppLoadExperimentOpen = experiments['specs.thunderbolt.appLoadInPanorama']

	if (isAppLoadExperimentOpen) {
		fedopsLogger.appLoadStarted = (params) => {
			reportPanoramaLoad(true, params?.paramsOverrides)
			appLoadStarted.call(fedopsLogger, params)
		}

		fedopsLogger.appLoaded = (params) => {
			reportPanoramaLoad(false, params?.paramsOverrides)
			appLoaded.call(fedopsLogger, params)
		}
	}

	return fedopsLogger
}
